/**
 * Pieces the live panel and the report share (BA-85): the step picture, the name row,
 * a fact, and the step list.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ImageOutlined as ImageIcon,
  SkipNext as SkipNextIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action } from '../../types/action';
import { Schedule } from '../../types/schedule';
import { SentSchedule } from '../../types/sentSchedule';
import { actionsService } from '../../services/actionsService';
import { SentStep, formatTime, rowStatus } from '../../utils/sentSchedule';
import { TYPE_COLORS } from '../schedules/ScheduleCard';

/** Signed thumbnail URLs for the steps' actions, in one request per set of actions */
export function useStepPictures(deviceId: string, steps: SentStep[], actions: Action[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const ids = useMemo(() => {
    const withPicture = new Set(actions.filter((a) => a.thumbnailUrl || a.pictureUrl).map((a) => a.id));
    return Array.from(new Set(steps.map((s) => s.actionId).filter((id): id is string => !!id && withPicture.has(id))))
      .sort();
  }, [steps, actions]);
  const key = ids.join(',');

  useEffect(() => {
    let alive = true;
    if (!deviceId || !key) {
      setUrls({});
      return;
    }
    actionsService.getBatchSignedPictureUrls(deviceId, key.split(','), true)
      .then((response) => {
        if (!alive) return;
        setUrls(Object.fromEntries(Object.entries(response.urls ?? {}).map(([id, info]) => [id, info.url])));
      })
      .catch((err) => console.error('Error fetching step pictures:', err));
    return () => { alive = false; };
  }, [deviceId, key]);

  return urls;
}

/** The step's action thumbnail; a nested schedule's emoji; or the grey placeholder */
export const StepPicture: React.FC<{
  step: SentStep;
  size: number;
  pictures: Record<string, string>;
  schedules?: Schedule[];
}> = ({ step, size, pictures, schedules }) => {
  const url = step.actionId ? pictures[step.actionId] : undefined;
  const nested = step.stepType === 'schedule' && step.scheduleId
    ? schedules?.find((s) => s.id === step.scheduleId)
    : undefined;
  const emoji = step.stepType === 'schedule' ? (nested?.icon?.emoji || '📋') : undefined;
  const box = { width: size, height: size, borderRadius: 1, flexShrink: 0 };
  if (url) {
    return <Box component="img" src={url} alt="" sx={{ ...box, objectFit: 'cover' }} />;
  }
  return (
    <Box
      aria-hidden
      sx={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}
    >
      {emoji
        ? <Box component="span" sx={{ fontSize: size * 0.55, lineHeight: 1 }}>{emoji}</Box>
        : <ImageIcon sx={{ fontSize: size * 0.4, color: 'grey.400' }} />}
    </Box>
  );
};

/** The schedule's icon, name and type chip, plus an optional status chip */
export const NameRow: React.FC<{ node: SentSchedule; chip?: React.ReactNode }> = ({ node, chip }) => {
  const { t } = useTranslation();
  const type = node.schedule?.type;
  const emoji = node.schedule?.icon?.type === 'emoji' && node.schedule.icon.emoji ? node.schedule.icon.emoji : '📋';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
      <Box component="span" aria-hidden sx={{ fontSize: 32, lineHeight: 1 }}>{emoji}</Box>
      <Typography variant="h5" component="h3" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
        {node.schedule?.name}
      </Typography>
      {type && (
        <Chip
          label={t(`schedules.types.${type}`)}
          size="small"
          sx={{ bgcolor: TYPE_COLORS[type] ?? '#757575', color: 'white', height: 20,
            '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
        />
      )}
      {chip}
    </Box>
  );
};

export const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" component="div" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography variant="body1" component="div">{children}</Typography>
  </Box>
);

export const Tokens: React.FC<{ count: number }> = ({ count }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <StarIcon sx={{ fontSize: 18, color: '#f9a825' }} />
    {count}
  </Box>
);

/** The step list with done times; the live current row is tinted (C1) */
export const StepList: React.FC<{
  node: SentSchedule;
  steps: SentStep[];
  live: boolean;
  pictures: Record<string, string>;
  schedules?: Schedule[];
}> = ({ node, steps, live, pictures, schedules }) => {
  const { t, i18n } = useTranslation();
  const firstThen = node.schedule?.type === 'first_then';
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('sentSchedule.steps')}</Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {steps.map((step, index) => {
          const status = rowStatus(node, step, index, live);
          let label = '';
          let shown: React.ReactNode = null;
          if (status.kind === 'done' || status.kind === 'skipped') {
            label = t(`sentSchedule.row.${status.kind}`, { time: formatTime(status.at, i18n.language) });
            shown = (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                color: status.kind === 'done' ? 'success.dark' : 'text.secondary' }}>
                {status.kind === 'done'
                  ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  : <SkipNextIcon sx={{ fontSize: 18 }} />}
                {label}
              </Box>
            );
          } else if (status.kind === 'now') {
            label = t('sentSchedule.row.now');
            shown = <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>● {label}</Box>;
          } else if (status.kind === 'notDone') {
            label = t('sentSchedule.row.notDone');
            shown = <Box component="span" sx={{ color: 'text.secondary' }}>{label}</Box>;
          }
          const reached = status.kind !== 'blank';
          return (
            <Box
              component="li"
              key={step.id}
              aria-label={label ? t('sentSchedule.row.aria', { title: step.title, status: label }) : step.title}
              aria-current={status.kind === 'now' ? 'step' : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 56, p: 1,
                borderBottom: index < steps.length - 1 ? 1 : 0, borderColor: 'divider',
                bgcolor: status.kind === 'now' ? 'rgba(25,118,210,.08)' : undefined,
              }}
            >
              <StepPicture step={step} size={40} pictures={pictures} schedules={schedules} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {firstThen && (
                  <Typography variant="caption" component="div" color="text.secondary">
                    {t(index === 0 ? 'sentSchedule.first' : 'sentSchedule.then')}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{ overflowWrap: 'anywhere', color: reached || !live ? 'text.primary' : 'text.secondary' }}
                >
                  {step.title}
                </Typography>
              </Box>
              <Typography variant="body2" component="span" sx={{ flexShrink: 0, textAlign: 'right' }}>
                {shown}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
